/**
 * Renders ONE JSON-declared control from the builder config.
 *
 * The whole reason the Design Builder can offer every chart's format options
 * without duplicating the Layout Builder: across all ~60 controls there are
 * only five kinds — `text`, `select`, `toggle`, `number`, `color`. This handles
 * them, so the inspector is a loop rather than a hand-written form, and a
 * control added to `dvm-layout-builder.json` or `charts/*.json` shows up here
 * with no code change at all.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../../shared/ss-select-tooltip.directive';

import type { FormatControl, LabelValueOption } from '../../../shared/self-service-config.types';

@Component({
  selector: 'app-design-control-renderer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './design-control-renderer.component.html',
  styleUrl: './design-control-renderer.component.scss',
})
export class DesignControlRendererComponent {
  readonly control = input.required<FormatControl>();
  /** Current value bag (the widget's `appearance`). */
  readonly values = input<Record<string, unknown>>({});
  /** Option lists from `layout.options`, for controls with an `optionsKey`. */
  readonly options = input<Record<string, unknown>>({});

  readonly valueChange = output<{ key: string; value: unknown }>();

  readonly kind = computed(() => this.control().control ?? 'text');

  /** Text-like controls (anything but a select / number / colour / toggle)
   *  take the label-above-box layout, so a URL has the whole panel width. */
  readonly stacked = computed(() => !['select', 'number', 'color', 'toggle'].includes(this.kind()));

  /** `width: 'half'` in the config → half the panel width, so two short
   *  fields (an axis prefix + suffix) share one row. */
  readonly half = computed(() => this.control().width === 'half');

  /** Current value, falling back to the control's declared default. */
  readonly value = computed<unknown>(() => {
    const control = this.control();
    const current = this.values()[control.key];
    return current === undefined || current === null ? control.default : current;
  });

  readonly textValue = computed(() => String(this.value() ?? ''));

  /** Hover text: the config's hint when it has one; else the label only when
   *  it is long enough to be clipped in the row; else nothing. */
  readonly tooltip = computed(() => {
    const control = this.control() as { hint?: string; label: string };
    const hint = String(control.hint ?? '').trim();
    if (hint) return hint;
    return control.label.length > 24 && !this.stacked() ? control.label : '';
  });
  readonly numberValue = computed(() => Number(this.value() ?? 0) || 0);
  readonly boolValue = computed(() => this.value() === true);
  readonly colorValue = computed(() => {
    const raw = String(this.value() ?? '');
    // <input type="color"> only accepts #rrggbb; anything else (a token, an
    // rgba(), or empty) would silently reset it to black, so fall back to a
    // neutral grey and let the user pick from there.
    return /^#[0-9a-f]{6}$/i.test(raw) ? raw : '#667085';
  });

  /**
   * A `column-select` picks one of the widget's own columns. The inspector
   * passes them under `tableColumns`; without any, it degrades to a text box.
   */
  readonly columnOptions = computed<LabelValueOption[]>(() => {
    const raw = this.options()['tableColumns'];
    return Array.isArray(raw) ? (raw as LabelValueOption[]) : [];
  });
  readonly isColumnSelect = computed(() => this.kind() === 'column-select' && this.columnOptions().length > 0);

  /** Resolved option list for a select. */
  readonly selectOptions = computed<LabelValueOption[]>(() => {
    const key = this.control().optionsKey;
    if (!key) return [];
    const raw = this.options()[key];
    if (!Array.isArray(raw)) return [];
    // Option lists appear both as {label,value} objects and as bare strings.
    return raw.map((entry) =>
      typeof entry === 'string'
        ? { label: entry, value: entry }
        : (entry as LabelValueOption),
    );
  });

  // ── `row-filter`: "Show only rows where" as column + value dropdowns ──────
  // Stored as the same text the table reads: `col = a | b; col2 = c`.

  readonly isRowFilter = computed(() => this.kind() === 'row-filter' && this.columnOptions().length > 0);

  readonly rowRules = computed<Array<{ field: string; values: string[] }>>(() =>
    this.textValue().split(';').map((part) => {
      const at = part.indexOf('=');
      if (at < 0) return null;
      const field = part.slice(0, at).trim();
      const values = part.slice(at + 1).split('|').map((value) => value.trim()).filter(Boolean);
      return field ? { field, values } : null;
    }).filter((rule): rule is { field: string; values: string[] } => !!rule));

  /** The values offered for a rule's column (plus the ones it already holds). */
  rowValueOptions(field: string, held: string[]): string[] {
    const all = (this.options()['tableColumnValues'] ?? {}) as Record<string, string[]>;
    const listed = all[field] ?? [];
    return [...held.filter((value) => !listed.includes(value)), ...listed];
  }

  /** ng-select `addTag`: a value typed in ({userEmail}, one not loaded yet). */
  readonly addRowValue = (term: string) => term.trim();

  private emitRules(rules: Array<{ field: string; values: string[] }>): void {
    this.emit(rules
      .filter((rule) => rule.field)
      .map((rule) => `${rule.field} = ${rule.values.join(' | ')}`)
      .join('; '));
  }

  setRuleField(index: number, field: string): void {
    // A new column starts with no values: the old ones belonged to the old column.
    this.emitRules(this.rowRules().map((rule, i) => (i === index ? { field, values: [] } : rule)));
  }

  setRuleValues(index: number, values: string[]): void {
    this.emitRules(this.rowRules().map((rule, i) => (i === index ? { ...rule, values: values ?? [] } : rule)));
  }

  addRule(): void {
    const used = new Set(this.rowRules().map((rule) => rule.field));
    const next = this.columnOptions().find((option) => !used.has(String(option.value)));
    this.emitRules([...this.rowRules(), { field: String(next?.value ?? this.columnOptions()[0].value), values: [] }]);
  }

  removeRule(index: number): void {
    this.emitRules(this.rowRules().filter((_, i) => i !== index));
  }

  trackByIndex(index: number): number {
    return index;
  }

  readonly min = computed(() => this.control().min ?? null);
  readonly max = computed(() => this.control().max ?? null);
  readonly step = computed(() => this.control().step ?? 1);

  emit(value: unknown): void {
    this.valueChange.emit({ key: this.control().key, value });
  }

  /** The column picker's own clear button: empty the value and keep the list shut. */
  clearPicker(picker: NgSelectComponent): void {
    picker.close();
    this.emit('');
  }

  emitNumber(raw: unknown): void {
    const parsed = Number(raw);
    this.emit(Number.isFinite(parsed) ? parsed : 0);
  }

  trackByValue(_: number, option: LabelValueOption): string {
    return String(option.value);
  }
}
